/**
 * AUTH-002 — the session controller: one owner for "is this user still signed in, and is
 * their token good enough to send?".
 *
 * Responsibilities (spec §5 / §6.1):
 *   - keep the token fresh for as long as the user is signed in, idle or not (item 1);
 *   - end the session at 15 minutes without input in any tab (item 2, D6);
 *   - on wake / tab focus, check last activity FIRST (item 2);
 *   - guarantee ≥ 60 s of token life before a request is sent (item 2a);
 *   - sign out by revoking THIS device's session on the server while the token is still
 *     valid, then clear local tokens through the single clearStoredTokens() (items 3, 8);
 *   - after a 401, refresh once and let the caller resend; sign out if that fails (item 4);
 *   - a dead refresh token ends in exactly one local sign-out, with no retry (item 6).
 *
 * Token refresh itself is the library's: `auth.getSession()` refreshes when the token is
 * within 90 s of expiry, serialised across tabs by the Navigator lock and re-reading the
 * shared cookie inside the lock, so two tabs never spend the same refresh token. This
 * module never parses a JWT.
 *
 * Pure: auth client, activity tracker, clock, token clearing and navigation are injected.
 */
import {
  IDLE_TIMEOUT_MS,
  MIN_TOKEN_LIFE_SECONDS,
  SIGN_OUT_REVOKE_TIMEOUT_MS,
  WAKE_GAP_MS,
  loginUrl,
  type EndReason,
} from './session-policy';

export type SessionLike = { access_token: string; expires_at?: number | null };
export type AuthResultLike = { data: { session: SessionLike | null }; error: unknown };

/** The slice of the supabase-js auth client this module uses. */
export interface AuthLike {
  getSession(): Promise<AuthResultLike>;
  refreshSession(): Promise<AuthResultLike>;
  signOut(options: { scope: 'local' }): Promise<{ error: unknown }>;
}

export interface ActivityLike {
  lastActivityAt(): number;
}

export type Freshness =
  | { status: 'ok'; token: string }
  /** The session is over (or being ended): do not send. */
  | { status: 'ended' }
  /** Refresh could not be attempted right now (network down, rate-limit back-off). The session is kept. */
  | { status: 'unavailable' };

export interface ControllerDeps {
  auth: AuthLike;
  activity: ActivityLike;
  now: () => number;
  clearTokens: () => void;
  navigate: (url: string) => void;
  currentPath?: () => string;
  warn?: (message: string) => void;
}

export interface SessionController {
  /** Begin managing a signed-in session (called once a session exists). */
  start(): void;
  /** Stop managing without signing out (component unmount). */
  stop(): void;
  isActive(): boolean;
  isEnding(): boolean;
  /** True when no tab has seen input for IDLE_TIMEOUT_MS. */
  isIdleExpired(): boolean;
  /** Pre-request guard: idle check, then a token with at least `minLifeSeconds` left. */
  ensureFresh(minLifeSeconds?: number): Promise<Freshness>;
  /** After a 401: one refresh (unless another caller already refreshed past `failedToken`). */
  refreshAfter401(failedToken?: string | null): Promise<Freshness>;
  /** End the session. Idempotent: only the first call does anything. */
  endSession(reason: EndReason, options?: { revoke?: boolean }): Promise<void>;
  /** Periodic tick: wake detection, idle check, keep-alive refresh. */
  tick(): Promise<void>;
  /** visibilitychange (visible) / focus / pageshow / online. */
  onWake(): Promise<void>;
  /** The auth client emitted SIGNED_OUT. */
  onSignedOut(): Promise<void>;
}

/**
 * A failed refresh is either "the refresh token is dead" (stop: sign out once) or "could
 * not ask right now" (keep the session, try later). auth-js marks the latter as
 * AuthRetryableFetchError (network failure, 502/503/504, and the 429 the fetch wrapper
 * converts); everything else — `refresh_token_not_found`, `invalid_grant`,
 * `session_not_found`, any 4xx — is dead.
 */
export function classifyRefreshError(error: unknown): 'dead' | 'unavailable' {
  if (!error || typeof error !== 'object') return 'dead';
  const e = error as { name?: string; status?: number; __isAuthError?: boolean };
  if (e.name === 'AuthRetryableFetchError') return 'unavailable';
  if (typeof e.status === 'number' && (e.status === 0 || e.status === 429 || e.status >= 500)) {
    return 'unavailable';
  }
  return 'dead';
}

async function withTimeout<T>(p: Promise<T>, ms: number): Promise<T | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      p,
      new Promise<undefined>((resolve) => {
        timer = setTimeout(() => resolve(undefined), ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

function errorMessage(error: unknown): string {
  return error && typeof error === 'object' && 'message' in error
    ? String((error as { message: unknown }).message)
    : String(error);
}

export function createSessionController(deps: ControllerDeps): SessionController {
  let active = false;
  let ending = false;
  let lastTickAt = deps.now();
  let refreshing: Promise<Freshness> | null = null;
  const warn = deps.warn ?? (() => {});

  const lifeSeconds = (s: SessionLike): number =>
    typeof s.expires_at === 'number' ? s.expires_at - deps.now() / 1000 : Number.POSITIVE_INFINITY;

  const isIdleExpired = (): boolean =>
    active && deps.now() - deps.activity.lastActivityAt() >= IDLE_TIMEOUT_MS;

  /** Turn an auth result into a decision; a dead result ends the session. */
  const settle = async (result: AuthResultLike, minLife: number): Promise<Freshness | null> => {
    const s = result.data.session;
    if (s && s.access_token && lifeSeconds(s) >= minLife)
      return { status: 'ok', token: s.access_token };
    if (s) return null; // a session, but not enough life: caller decides whether to force a refresh
    if (result.error && classifyRefreshError(result.error) === 'unavailable')
      return { status: 'unavailable' };
    // No session and no retryable error: the refresh token is dead or the session was
    // ended elsewhere. One local sign-out; nothing to revoke.
    await endSession('expired', { revoke: false });
    return { status: 'ended' };
  };

  const forceRefresh = (): Promise<Freshness> => {
    if (!refreshing) {
      refreshing = (async () => {
        try {
          const r = await deps.auth.refreshSession();
          const decided = await settle(r, 0);
          // A refresh that returned a session with no life left is as good as dead.
          if (decided) return decided;
          await endSession('expired', { revoke: false });
          return { status: 'ended' } as Freshness;
        } catch (e) {
          // auth-js throws only for non-auth errors (e.g. storage failure); treat as transient.
          warn(`AUTH-002: refresh threw: ${errorMessage(e)}`);
          return { status: 'unavailable' } as Freshness;
        } finally {
          refreshing = null;
        }
      })();
    }
    return refreshing;
  };

  async function ensureFresh(minLifeSeconds: number = MIN_TOKEN_LIFE_SECONDS): Promise<Freshness> {
    if (ending) return { status: 'ended' };
    if (isIdleExpired()) {
      await endSession('idle');
      return { status: 'ended' };
    }
    // The library refreshes here when the token is within its 90 s margin.
    const decided = await settle(await deps.auth.getSession(), minLifeSeconds);
    if (decided) return decided;
    // A caller asked for more headroom than the library margin (long exports): force it.
    return forceRefresh();
  }

  async function refreshAfter401(failedToken?: string | null): Promise<Freshness> {
    if (ending) return { status: 'ended' };
    // Another request (or tab) may already have refreshed: if the stored token is not the
    // one that was refused, resend with it rather than spend another refresh.
    const current = await deps.auth.getSession();
    const s = current.data.session;
    if (
      s &&
      s.access_token &&
      s.access_token !== failedToken &&
      lifeSeconds(s) >= MIN_TOKEN_LIFE_SECONDS
    ) {
      return { status: 'ok', token: s.access_token };
    }
    if (!s && !current.error) {
      await endSession('expired', { revoke: false });
      return { status: 'ended' };
    }
    return forceRefresh();
  }

  async function revokeThisDevice(): Promise<void> {
    // Make sure the token is valid before calling /logout: getSession refreshes it if it
    // is close to expiry. Refresh never paused while idle, so this normally costs nothing.
    const fresh = await deps.auth.getSession();
    if (!fresh.data.session) return; // nothing left to revoke
    const { error } = await deps.auth.signOut({ scope: 'local' });
    if (error)
      warn(
        `AUTH-002: server sign-out failed (local tokens cleared anyway): ${errorMessage(error)}`
      );
  }

  async function endSession(reason: EndReason, options: { revoke?: boolean } = {}): Promise<void> {
    if (ending) return;
    ending = true;
    active = false;
    const revoke = options.revoke ?? reason !== 'expired';
    try {
      if (revoke) await withTimeout(revokeThisDevice(), SIGN_OUT_REVOKE_TIMEOUT_MS);
    } catch (e) {
      warn(`AUTH-002: server sign-out threw (local tokens cleared anyway): ${errorMessage(e)}`);
    } finally {
      deps.clearTokens();
      deps.navigate(loginUrl(reason, deps.currentPath?.()));
    }
  }

  async function onWake(): Promise<void> {
    lastTickAt = deps.now();
    if (!active || ending) return;
    if (isIdleExpired()) {
      await endSession('idle');
      return;
    }
    await ensureFresh();
  }

  return {
    start() {
      if (ending) return;
      active = true;
      lastTickAt = deps.now();
    },
    stop() {
      active = false;
    },
    isActive: () => active,
    isEnding: () => ending,
    isIdleExpired,
    ensureFresh,
    refreshAfter401,
    endSession,
    async tick() {
      const now = deps.now();
      const gap = now - lastTickAt;
      if (!active || ending) {
        lastTickAt = now;
        return;
      }
      if (gap > WAKE_GAP_MS) return onWake();
      lastTickAt = now;
      // Keep-alive: ensureFresh checks idle, then lets the library refresh inside its margin.
      await ensureFresh();
    },
    onWake,
    async onSignedOut() {
      // Our own sign-out emits SIGNED_OUT too; only an unexpected one (dead refresh token,
      // another tab signed out) needs handling, and only once.
      if (ending || !active) return;
      await endSession('expired', { revoke: false });
    },
  };
}
